import { createContext, useContext } from 'react';
import { ClockMonospace } from './ClockMonospace';

/**
 * L'OROLOGIO SCENDE PER CONTESTO, NON PER PROP, E IL MOTIVO E' MISURATO.
 *
 * `tickClock` ricrea l'oggetto `clockState` quattro volte al secondo. Finche'
 * quel valore era una prop di `GameScreen`, ogni tick ri-renderizzava l'intero
 * albero di partita — anche mentre un dito stava trascinando un pezzo, perche'
 * `useClockTick` NON si ferma durante il trascinamento (e' deliberato: in una
 * partita locale quel tick e' l'unica cosa che fa cadere la bandierina).
 *
 * Misurato con `scripts/measure-drag.mjs` su un BUILD DI PRODUZIONE, gesto
 * veloce a 360px, freno 20x, mediana di 3 — cioe' il profilo di un telefono
 * vecchio, che e' da dove arriva la segnalazione:
 *
 *                                    JS     fotogramma peggiore   oltre 50ms
 *   partita SENZA orologio         403ms              17ms            0
 *   partita CON orologio           866ms              67ms            6
 *
 * Zero fotogrammi lunghi senza orologio, sei con: a parita' di tutto il resto.
 * E 67ms sono quattro fotogrammi persi di fila, che e' esattamente cio' che si
 * sente come «scatto».
 *
 * ⚠️ IN SVILUPPO LO STESSO CONFRONTO DICEVA 44 INVECE DI 6. Il server di
 * sviluppo crea gli elementi con `jsxDEV`, che non esiste in produzione: il
 * difetto e' reale, ma lo sviluppo lo gonfia di circa sette volte. Chi rimisura
 * qui usi `vite build` + `vite preview`, come fa gia' `b06c1eab`.
 *
 * COSA PASSA DI QUA E COSA NO. Di qua passano i soli MILLISECONDI, che cambiano
 * a ogni tick e servono a due sole foglie (i quadranti). Tutto il resto —
 * «il mio orologio e' attivo», «e' in tempo basso» — resta una PROP booleana,
 * perche' cambia di rado e tenerla fuori dal contesto e' cio' che permette a
 * `memo(GameScreen)` di fermarsi davvero.
 *
 * I valori sono gia' risolti in «mio» e «avversario»: chi li consuma non deve
 * sapere di che colore gioca, e cosi' il quadrante non ha bisogno di
 * `playerColor`.
 */
export interface ValoriOrologio {
  /** Millisecondi di chi guarda, o `null` se la partita non ha orologio. */
  mio: number | null;
  avversario: number | null;
}

const SENZA_OROLOGIO: ValoriOrologio = { mio: null, avversario: null };

export const OrologioContext = createContext<ValoriOrologio>(SENZA_OROLOGIO);

export function useOrologio(): ValoriOrologio {
  return useContext(OrologioContext);
}

/**
 * Il quadrante: l'UNICO nodo che si risveglia a ogni tick.
 *
 * Torna `null` quando non c'e' orologio, cosi' chi lo monta puo' decidere la
 * cornice guardando un booleano stabile invece del valore che cambia.
 */
export function QuadranteOrologio({
  lato, isActive, isLow,
}: {
  lato: 'mio' | 'avversario';
  isActive: boolean;
  isLow: boolean;
}) {
  const valori = useOrologio();
  const tempo = lato === 'mio' ? valori.mio : valori.avversario;
  if (tempo === null) return null;
  return <ClockMonospace time={tempo} isActive={isActive} isLow={isLow} />;
}
